import React, { useMemo } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip
} from '@mui/material'

export const getRatingNodeLabel = (guid, ratingNodes) => {
  if (!guid) return null
  const node = (ratingNodes || []).find(n => n.guid === guid)
  if (!node) return guid
  if (node.label) return node.label
  return `${node.benefit_code}${
    node.package_code ? ` (${node.package_code})` : ''
  } — ${node.node_type}`
}

export const RELATIONSHIP_TYPES = [
  'Principal',
  'Spouse',
  'Child',
  'Parent',
  'Extended'
]

export const emptyAllowedRelationships = () => {
  const result = {}
  RELATIONSHIP_TYPES.forEach(rel => {
    result[rel] = { enabled: false, min_age: '', max_age: '', max_count: '' }
  })
  return result
}

export const formatValueForDisplay = value => {
  if (value === null || value === undefined) return ''
  return value.toString()
}

export const validateRelationships = allowedRelationships => {
  const errors = {}
  const enabledRels = RELATIONSHIP_TYPES.filter(
    rel => allowedRelationships[rel].enabled
  )
  if (enabledRels.length === 0) {
    errors.relationship = 'At least one eligible relationship must be selected'
  }
  enabledRels.forEach(rel => {
    const entry = allowedRelationships[rel]
    if (
      entry.min_age === '' ||
      entry.min_age === null ||
      entry.min_age === undefined
    ) {
      errors[`rel_min_age_${rel}`] = 'Min age required'
    } else if (parseInt(entry.min_age) < 0) {
      errors[`rel_min_age_${rel}`] = 'Min age cannot be negative'
    }
    if (
      entry.max_age === '' ||
      entry.max_age === null ||
      entry.max_age === undefined
    ) {
      errors[`rel_max_age_${rel}`] = 'Max age required'
    } else if (parseInt(entry.max_age) <= 0) {
      errors[`rel_max_age_${rel}`] = 'Max age must be > 0'
    }
    if (
      entry.max_count === '' ||
      entry.max_count === null ||
      entry.max_count === undefined
    ) {
      errors[`rel_max_count_${rel}`] = 'Max count required'
    } else if (parseInt(entry.max_count) <= 0) {
      errors[`rel_max_count_${rel}`] = 'Max count must be > 0'
    }
  })
  return errors
}

export const RelationshipAgeRow = ({
  rel,
  entry,
  onChange,
  hasFieldError,
  getFieldError
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '120px 1fr 1fr 1fr',
      gap: 2,
      mt: 2,
      alignItems: 'flex-start'
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 500, pt: 1 }}>
      {rel}
    </Typography>
    <TextField
      label="Min Age"
      type="number"
      size="small"
      value={entry.min_age}
      onChange={onChange(rel, 'min_age')}
      error={hasFieldError(`rel_min_age_${rel}`)}
      helperText={getFieldError(`rel_min_age_${rel}`)}
    />
    <TextField
      label="Max Age"
      type="number"
      size="small"
      value={entry.max_age}
      onChange={onChange(rel, 'max_age')}
      error={hasFieldError(`rel_max_age_${rel}`)}
      helperText={getFieldError(`rel_max_age_${rel}`)}
    />
    <TextField
      label="Max Count"
      type="number"
      size="small"
      value={entry.max_count}
      onChange={onChange(rel, 'max_count')}
      error={hasFieldError(`rel_max_count_${rel}`)}
      helperText={getFieldError(`rel_max_count_${rel}`)}
    />
  </Box>
)

/**
 * Picker for a benefit/add-on's cover_/premium_/waiting_period_rating_node_guid.
 *
 * `appliesTo` ('cover' | 'premium' | 'waiting_period') scopes the options to
 * nodes actually meant for that target — without it, the Cover and Premium
 * pickers showed the exact same list (both just filtered by nodeTypes),
 * letting a user wire a premium node into a cover FK by mistake.
 *
 * `packageCode` (the current benefit's package code, or null for an add-on)
 * further scopes to nodes belonging to that same package — a rating node's
 * own uniqueness is keyed on (product, package_code, benefit_code), so a node
 * from a different package is never a valid choice here.
 *
 * `_package` nodes (benefit_code === "_package") are never shown — they're
 * resolved by the evaluator via a direct key lookup, not this FK, so
 * selecting one here would do nothing.
 */
export const RatingNodeSelect = ({
  value,
  onChange,
  ratingNodes,
  nodeTypes,
  appliesTo,
  packageCode,
  placeholder
}) => {
  const filtered = useMemo(
    () =>
      (ratingNodes || []).filter(
        n =>
          nodeTypes.includes(n.node_type) &&
          n.benefit_code !== '_package' &&
          (!appliesTo || n.applies_to === appliesTo) &&
          (n.package_code || null) === (packageCode || null)
      ),
    [ratingNodes, nodeTypes, appliesTo, packageCode]
  )
  const nodeMap = useMemo(() => {
    const map = {}
    ;(ratingNodes || []).forEach(n => {
      map[n.guid] = n
    })
    return map
  }, [ratingNodes])

  return (
    <FormControl fullWidth size="small" variant="outlined">
      <Select
        displayEmpty
        value={value}
        onChange={onChange}
        renderValue={val => {
          if (!val) return <span style={{ color: '#999' }}>{placeholder}</span>
          const node = nodeMap[val]
          return node ? getRatingNodeLabel(val, ratingNodes) : val
        }}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {filtered.map(n => (
          <MenuItem key={n.guid} value={n.guid}>
            {getRatingNodeLabel(n.guid, ratingNodes)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

/**
 * Read-only summary of the cover/premium/waiting-period rating nodes attached
 * to a benefit or add-on, for list/card views (as opposed to RatingNodeSelect,
 * which is the editable picker used inside the create/edit form).
 */
export const RatingSummary = ({
  coverRatingNodeGuid,
  premiumRatingNodeGuid,
  waitingPeriodRatingNodeGuid,
  ratingNodes
}) => {
  const entries = [
    ['Cover', coverRatingNodeGuid],
    ['Premium', premiumRatingNodeGuid],
    ['Waiting period', waitingPeriodRatingNodeGuid]
  ].filter(([, guid]) => !!guid)

  if (entries.length === 0) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: 'italic' }}
      >
        No rating nodes attached
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {entries.map(([label, guid]) => (
        <Chip
          key={label}
          size="small"
          label={`${label}: ${getRatingNodeLabel(guid, ratingNodes)}`}
          sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
        />
      ))}
    </Box>
  )
}
